import { useEffect, useState } from "react";
import { Card, Form, Row, Col, Spinner, Button, Modal } from "react-bootstrap";
import api from "../../../axiosConfig";
import * as XLSX from "xlsx";
import "./css/ToolStock.css";
import { popup } from "../../common/popup";

export default function StockToolsPage() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("ALL");
  const [keyword, setKeyword] = useState("");

  // State modal edit
  const [showModal, setShowModal] = useState(false);
  const [editTool, setEditTool] = useState(null);
  const [stockQty, setStockQty] = useState(0);
  const [spec, setSpec] = useState("-");
  const [saving, setSaving] = useState(false);
  const [kode, setKode] = useState(""); // pastikan ini di atas
  const [partNama, setPartNama] = useState(""); // pastikan ini di atas
  const [editCategory, setEditCategory] = useState("");
  const [isAddMode, setIsAddMode] = useState(false);
  const [lokasi, setLokasi] = useState("");

  // Kategori filter (dikelola admin, read-only di sisi maintenance)
  const [categories, setCategories] = useState([]);

  // Ambil data stock tools
  const fetchTools = async () => {
    setLoading(true);
    try {
      const res = await api.get("/stock-tools");
      setTools(res.data.data || []);
    } catch (err) {
      console.error("Gagal ambil tools:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get("/stock-tools/categories");
      setCategories(res.data.data || []);
    } catch (err) {
      console.error("Gagal ambil kategori:", err);
    }
  };

  useEffect(() => {
    fetchTools();
    fetchCategories();
  }, []);

  // Filter tools
  const filteredTools = tools.filter((t) => {
    const matchCategory =
      category === "ALL" || t.category?.toUpperCase() === category;
    const matchKeyword =
      !keyword ||
      [t.kode, t.part_name, t.category, t.spec, t.lokasi, t.stock_qty, t.status]
        .filter((v) => v !== null && v !== undefined)
        .join(" ")
        .toLowerCase()
        .includes(keyword.toLowerCase());
    return matchCategory && matchKeyword;
  });

  const getStockStatus = (qty) => {
    if (qty <= 0) return "O";
    if (qty <= 2) return "LOW";
    return "OK";
  };

  // ... kode sebelumnya tetap sama sampai bagian modal

  const handleAddClick = () => {
    setIsAddMode(true);
    setEditTool(null);
    setKode("");
    setPartNama("");
    setStockQty(0);
    setSpec("");
    setEditCategory("");
    setShowModal(true);
    setLokasi("");
  };

  // Buka modal edit
  const handleEditClick = (tool) => {
    setEditTool(tool);
    setStockQty(tool.stock_qty);
    setSpec(tool.spec || "-");
    setKode(tool.kode);
    setPartNama(tool.part_name);
    setEditCategory(tool.category?.toUpperCase().trim());
    setShowModal(true);
    setLokasi(tool.lokasi || "");
  };

  // Simpan update stock + kode + part_name
  const handleSave = async () => {
    setSaving(true);
    if (!editCategory) {
      popup.alert("Kategori harus dipilih!");
      setSaving(false);
      return;
    }

    try {
      let res;

      if (isAddMode) {
        // CREATE
        res = await api.post("/stock-tools", {
          kode,
          part_nama: partNama,
          stock_qty: stockQty,
          category: editCategory,
          spec,
        });
      } else {
        // UPDATE
        res = await api.put(`/stock-tools/${editTool.id}`, {
          kode,
          part_nama: partNama,
          stock_qty: stockQty,
          category: editCategory,
          spec,
          lokasi,
        });
      }

      if (res.data.success) {
        setShowModal(false);
        setEditTool(null);
        setIsAddMode(false);
        popup.success(isAddMode ? "Data berhasil disimpan" : "Data berhasil diperbarui");
        fetchTools();
      } else {
        popup.error(res.data.message || "Gagal menyimpan data");
      }
    } catch (err) {
      console.error(err);
      popup.error("Terjadi kesalahan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tool) => {
    const ok = await popup.confirm(`Yakin ingin menghapus ${tool.part_name}?`);

    if (!ok) return;

    try {
      const res = await api.delete(`/stock-tools/${tool.id}`);

      if (res.data.success) {
        popup.success("Data berhasil dihapus");
        fetchTools();
      } else {
        popup.error(res.data.message || "Gagal menghapus data");
      }
    } catch (err) {
      console.error(err);
      popup.error("Terjadi kesalahan saat menghapus");
    }
  };

  const handleDownloadExcel = () => {
    if (filteredTools.length === 0) {
      popup.alert("Data kosong, tidak ada yang bisa di-download");
      return;
    }

    const data = filteredTools.map((t, i) => ({
      No: i + 1,
      Kode: t.kode,
      "Part Name": t.part_name,
      Spec: t.spec || "-",
      Kategori: t.category,
      Stock: t.stock_qty,
      Status: getStockStatus(t.stock_qty),
      "Update Terakhir": t.updated_at
        ? new Date(t.updated_at).toLocaleString("id-ID")
        : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Tools");

    XLSX.writeFile(workbook, "DATA_STOCK_TOOLS.xlsx");
  };

  const sortedTools = [...filteredTools].sort((a, b) => {
    if (a.stock_qty === 0 && b.stock_qty !== 0) return -1;
    if (a.stock_qty !== 0 && b.stock_qty === 0) return 1;
    return 0; // selain itu urutan tetap
  });

  // Kategori filter dari backend (dikelola admin) + kategori yang ada di data
  const categoryOptions = [
    "ALL",
    ...Array.from(
      new Set([
        ...categories,
        ...tools.map((t) => t.category?.toUpperCase().trim()).filter(Boolean),
      ]),
    ),
  ];

  return (
    <div className="longlife-container d-flex flex-column">
      <Card className="shadow-sm flex-grow-1 d-flex flex-column">
        {/* ================= HEADER ================= */}
        <Card.Header className="bg-white">
          {/* JUDUL + BUTTON */}
          <Row className="align-items-center g-2">
            <Col xs={12} md>
              <h5 className="mb-0" style={{ fontSize: "26px" }}>
                DATA STOCK TOOLS
              </h5>
            </Col>

            <Col xs={12} md="auto" className="d-flex gap-2 flex-wrap">
              <Button size="sm" onClick={handleAddClick}>
                + Tambah Tools
              </Button>

              <Button size="sm" variant="success" onClick={handleDownloadExcel}>
                Download Excel
              </Button>
            </Col>
          </Row>

          {/* FILTER + SEARCH */}
          <Row className="mt-3 g-2 align-items-center">
            <Col xs={12} lg={8}>
              <div className="d-flex flex-wrap gap-2">
                {categoryOptions.map((cat) => (
                  <button
                    key={cat}
                    className={`  btn btn-sm ${
                      category === cat
                        ? "btn text-white bg-primary"
                        : "btn-light"
                    }`}
                    onClick={() => setCategory(cat)}
                    style={{ border: "1px solid grey" }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </Col>

            <Col xs={12} lg={4} className="d-flex gap-2">
              <Form.Control
                size="sm"
                placeholder="🔍 Cari..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  setCategory("ALL");
                  setKeyword("");
                }}
              >
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Header>

        {/* ================= TABLE ================= */}
        <Card.Body>
          <div className="table-container">
            <table className="preview-table">
              <thead className="table-light sticky-top">
                <tr>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>No</th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Part Name
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Kode / Spec
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Lokasi
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Kategori
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>
                    Stock
                  </th>
                  <th style={{ background: "#1e3a8a", color: "#fff" }}>Aksi</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-4">
                      <Spinner animation="border" size="sm" /> Loading...
                    </td>
                  </tr>
                ) : filteredTools.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-4 text-center">
                      Data tidak ditemukan
                    </td>
                  </tr>
                ) : (
                  sortedTools.map((t, i) => {
                    return (
                      <tr
                        key={t.id}
                        className={t.stock_qty === 0 ? "stock-zero" : ""}
                      >
                        <td className="text-center">{i + 1}</td>
                        <td className="cell-left">{t.part_name}</td>
                        <td className="cell-left">{t.spec || "-"}</td>
                        <td>{t.lokasi || "-"}</td>
                        <td>{t.category}</td>
                        <td className="text-center">
                          <strong>{t.stock_qty}</strong>
                        </td>

                        <td>
                          <div className="d-flex justify-content-center gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleEditClick(t)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(t)}
                            >
                              Hapus
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      {/* Modal Edit */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Stock Tool</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Kode</Form.Label>
            <Form.Control
              value={kode}
              onChange={(e) => setKode(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Part Name</Form.Label>
            <Form.Control
              value={partNama}
              onChange={(e) => setPartNama(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Spec</Form.Label>
            <Form.Control
              value={spec}
              onChange={(e) => setSpec(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Lokasi</Form.Label>
            <Form.Control
              value={lokasi}
              onChange={(e) => setLokasi(e.target.value)}
              placeholder="Contoh: 1.A.1"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Kategori</Form.Label>
            <Form.Select
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
            >
              <option value="">-- Pilih Kategori --</option>
              {categoryOptions
                .filter((c) => c !== "ALL")
                .map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Stock Qty</Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={stockQty}
              onChange={(e) => setStockQty(Number(e.target.value))}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Menyimpan..." : "Simpan"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
